import axios from 'axios'
import type { PlatformStoresConfig, StoreOption } from '@/types/reviewPlatforms'

type PlatformSlug = 'deliveroo' | 'ubereats'

interface LinkedRow {
  storeId: number
  storeName: string
  externalStoreId: string
}

export function usePlatformConfig(
  slug: PlatformSlug,
  config: Ref<PlatformStoresConfig | undefined>,
  onSaved: () => void
) {
  const { t } = useI18n()
  const { user, stores } = useLocalStorage()
  const notification = useNotification()

  const linkModalOpen = ref(false)
  const editingStoreId = ref<number | null>(null)
  const unlinkConfirmStoreId = ref<number | null>(null)
  const isSaving = ref(false)

  const linked = computed<LinkedRow[]>(() =>
    (config.value?.stores ?? []).map((entry) => {
      const store = stores.value.find((s: StoreOption) => s.id === entry.storeId)
      return {
        storeId: entry.storeId,
        storeName: store?.name ?? `#${entry.storeId}`,
        externalStoreId: entry.externalStoreId
      }
    })
  )

  const linkedStoreIds = computed(() => linked.value.map((r) => r.storeId))
  const linkedExternalIds = computed(() => linked.value.map((r) => r.externalStoreId))

  const availableStoresForLink = computed(() =>
    stores.value.filter((s: StoreOption) => !linkedStoreIds.value.includes(s.id))
  )

  const editingRow = computed(() => linked.value.find((r) => r.storeId === editingStoreId.value) ?? null)

  const allStoresForEdit = computed(() =>
    stores.value.filter((s: StoreOption) => s.id === editingRow.value?.storeId || !linkedStoreIds.value.includes(s.id))
  )
  const canAddMore = computed(() => availableStoresForLink.value.length > 0)

  async function deleteStoreLink(storeId: number): Promise<void> {
    await axios.delete(`/merchants/${user.value.merchant.id}/review-managers/${slug}/stores/${storeId}`)
  }

  async function confirmUnlink() {
    const storeId = unlinkConfirmStoreId.value
    if (storeId === null) return
    isSaving.value = true
    try {
      await deleteStoreLink(storeId)
      unlinkConfirmStoreId.value = null
      notification.create('success', { text: t('pages.reviewPlatforms.configuration.shared.unlinkSuccess') })
      onSaved()
    } catch {
      notification.create('error', { text: t('pages.reviewPlatforms.configuration.shared.unlinkError') })
    } finally {
      isSaving.value = false
    }
  }

  return {
    linkModalOpen,
    editingStoreId,
    unlinkConfirmStoreId,
    isSaving,
    linked,
    linkedExternalIds,
    availableStoresForLink,
    allStoresForEdit,
    canAddMore,
    editingRow,
    deleteStoreLink,
    confirmUnlink
  }
}
